import React, { useEffect, useMemo, useState, useCallback } from 'react';
import PlaybookInstanceCard from './PlaybookInstanceCard';
import PlaybookCoordinationView from './PlaybookCoordinationView';
import ApprovalBadge from './ApprovalBadge';
import { usePlaybookInstances } from '../hooks/usePlaybookInstances';
import { useUserPermissions } from '@/hooks/useUserPermissions';
import { useAppStore } from '@/store/appStore';
import { useKeycloak } from '@react-keycloak/web';
import { playbookApi } from '../api/playbookApi';
import { PLAYBOOK_KEYFRAMES } from '../types/playbook.types';
import type { MatterDetailed } from '@/types/matters';
import type { AvailableTransition, PlaybookEntity } from '../types/playbook.types';

interface PlaybookTabProps {
  matter: MatterDetailed;
}

const PlaybookTab: React.FC<PlaybookTabProps> = ({ matter }) => {
  const { instances, loading, error, refresh } = usePlaybookInstances(matter.id);
  const { isAdmin } = useUserPermissions();
  const { keycloak } = useKeycloak();
  const { currentUserId } = useAppStore();

  const userId = currentUserId || keycloak.tokenParsed?.sub || '';

  // Load transitions for all instances (needed for coordination view)
  const [transitionsByInstance, setTransitionsByInstance] = useState<Record<string, AvailableTransition[]>>({});

  useEffect(() => {
    if (instances.length < 2) return;
    const load = async () => {
      const result: Record<string, AvailableTransition[]> = {};
      for (const inst of instances) {
        try {
          const res = await playbookApi.getAvailableTransitions(inst.id);
          const data = res as any;
          result[inst.id] = data.transitions || [];
        } catch {
          result[inst.id] = [];
        }
      }
      setTransitionsByInstance(result);
    };
    load();
  }, [instances]);

  // Count pending approvals across all instances
  const totalPendingApprovals = useMemo(() => {
    return Object.values(transitionsByInstance).reduce(
      (sum, transitions) =>
        sum + transitions.filter((t) => t.isCollaborative && !t.isAllowed).length,
      0,
    );
  }, [transitionsByInstance]);

  // ── Start Playbook picker state ──
  const [showPicker, setShowPicker] = useState(false);
  const [activePlaybooks, setActivePlaybooks] = useState<PlaybookEntity[]>([]);
  const [loadingPlaybooks, setLoadingPlaybooks] = useState(false);
  const [creating, setCreating] = useState(false);

  const openPicker = useCallback(async () => {
    setShowPicker(true);
    if (activePlaybooks.length > 0) return;
    setLoadingPlaybooks(true);
    try {
      const res = await playbookApi.listPlaybooks({ lifecycle: 'active', limit: 100 });
      const data = res as any;
      setActivePlaybooks(data.playbooks || []);
    } catch {
      // silent — user can see the empty list
    } finally {
      setLoadingPlaybooks(false);
    }
  }, [activePlaybooks.length]);

  const handleStartPlaybook = useCallback(async (playbookId: string) => {
    setCreating(true);
    try {
      await playbookApi.createInstance(matter.id, { playbookId });
      setShowPicker(false);
      refresh();
    } catch (err) {
      console.error('[PlaybookTab] Failed to create instance:', err);
    } finally {
      setCreating(false);
    }
  }, [matter.id, refresh]);

  // ── Start Playbook picker UI ──
  const pickerUI = showPicker && (
    <div className="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">Select a playbook to start</span>
        <button
          type="button"
          onClick={() => setShowPicker(false)}
          className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          Cancel
        </button>
      </div>
      {loadingPlaybooks ? (
        <div className="flex items-center gap-2 py-2">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-lex-accent border-t-transparent" />
          <span className="text-xs text-gray-500">Loading playbooks...</span>
        </div>
      ) : activePlaybooks.length === 0 ? (
        <p className="text-xs text-gray-400 py-2">No active playbooks available. Publish a playbook first.</p>
      ) : (
        <div className="space-y-1 max-h-48 overflow-y-auto">
          {activePlaybooks.map((pb) => (
            <button
              key={pb.id}
              type="button"
              disabled={creating}
              onClick={() => handleStartPlaybook(pb.id)}
              className="w-full text-left rounded-md border border-gray-100 px-3 py-2 hover:bg-lex-state-hover dark:border-gray-700 transition-colors disabled:opacity-50"
            >
              <div className="text-xs font-medium text-gray-900 dark:text-white">{pb.name}</div>
              {pb.description && (
                <div className="text-[10px] text-gray-500 dark:text-gray-400 line-clamp-1 mt-0.5">{pb.description}</div>
              )}
              <div className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">
                v{pb.version} · {pb.practice_areas?.join(', ') || 'General'}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-lex-accent border-t-transparent" />
        <span className="ml-3 text-sm text-gray-500 dark:text-gray-400">Loading playbooks...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-200">
        {error}
      </div>
    );
  }

  if (instances.length === 0) {
    return (
      <div className="space-y-4">
        <div className="text-center py-12">
          <div className="text-gray-400 dark:text-gray-500 mb-2">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No playbooks are active for this matter.
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 mb-4">
            Start a playbook manually or apply a task template with a workflow.
          </p>
          {!showPicker && (
            <button
              type="button"
              onClick={openPicker}
              className="rounded-md bg-lex-accent px-4 py-2 text-sm font-medium text-lex-accent-contrast hover:bg-lex-accent-hover"
            >
              Start Playbook
            </button>
          )}
        </div>
        {pickerUI}
      </div>
    );
  }

  return (
    <>
      <style>{PLAYBOOK_KEYFRAMES}</style>

      <div className="space-y-4" style={{ animation: 'fadeSlideIn 300ms ease-out' }}>
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              Active Playbooks ({instances.length})
            </h3>
            {totalPendingApprovals > 0 && <ApprovalBadge count={totalPendingApprovals} />}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={openPicker}
              className="rounded-md border border-lex-accent px-2.5 py-1 text-xs font-medium text-lex-accent hover:bg-lex-state-hover"
            >
              + Start Playbook
            </button>
            <button
              type="button"
              onClick={refresh}
              className="rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700"
            >
              Refresh
            </button>
          </div>
        </div>

        {/* Playbook picker */}
        {pickerUI}

        {/* Cross-playbook coordination */}
        {instances.length >= 2 && (
          <PlaybookCoordinationView
            instances={instances}
            transitionsByInstance={transitionsByInstance}
          />
        )}

        {/* Instance cards */}
        {instances.map((instance) => (
          <PlaybookInstanceCard
            key={instance.id}
            instance={instance}
            isAdmin={isAdmin}
            currentUserId={userId}
            onRefresh={refresh}
          />
        ))}
      </div>
    </>
  );
};

export default PlaybookTab;
